import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  TooltipProps,
} from 'recharts';
import { Activity, Loader2 } from 'lucide-react';

/**
 * Interface for health records data
 */
export interface HealthRecordsData {
  vaccinations: number;
  treatments: number;
  checkupsPending: number;
}

interface HealthRecordsChartProps {
  data: HealthRecordsData;
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Color scheme for health categories
 */
const COLORS = {
  vaccinations: '#10b981', // green
  treatments: '#ec4899', // pink
  checkupsPending: '#ef4444', // red
};

/**
 * Custom tooltip component
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700">{payload[0].name}</p>
        <p className="text-gray-600">
          Count: <span className="font-bold">{payload[0].value}</span>
        </p>
        <p className="text-xs text-gray-500">
          {((payload[0].value as number) / (payload[0].payload.total) * 100).toFixed(1)}% of total
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Custom label renderer for pie chart
 */
const renderLabel = (entry: any) => {
  const percent = ((entry.value / entry.payload.total) * 100).toFixed(0);
  return `${percent}%`;
};

/**
 * HealthRecordsChart - Pie/Donut chart showing health records breakdown
 *
 * Features:
 * - Displays distribution of vaccinations, treatments, and pending checkups
 * - Color-coded segments (green, pink, red)
 * - Interactive tooltips with percentages
 * - Percentage labels on chart segments
 * - Responsive design for all screen sizes
 * - Loading and error states
 *
 * @param data - Health records data object
 * @param loading - Loading state indicator
 * @param error - Error message if data fetch failed
 * @param height - Chart height in pixels (default: 350)
 */
const HealthRecordsChart: React.FC<HealthRecordsChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        <span className="ml-2 text-gray-600">Loading health records...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Transform data for pie chart
  const total = data.vaccinations + data.treatments + data.checkupsPending;

  // Empty state
  if (total === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No health records available</p>
        </div>
      </div>
    );
  }

  const chartData = [
    { name: 'Vaccinations', value: data.vaccinations, color: COLORS.vaccinations, total },
    { name: 'Treatments', value: data.treatments, color: COLORS.treatments, total },
    { name: 'Pending Checkups', value: data.checkupsPending, color: COLORS.checkupsPending, total },
  ].filter(item => item.value > 0); // Only show segments with data

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Activity className="w-5 h-5 mr-2 text-green-600" />
          Health Records Overview
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Distribution of health activities
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
          <p className="text-xs text-gray-600">Vaccinations</p>
          <p className="text-2xl font-bold text-green-600">{data.vaccinations}</p>
        </div>
        <div className="bg-pink-50 p-3 rounded-lg border-l-4 border-pink-500">
          <p className="text-xs text-gray-600">Treatments</p>
          <p className="text-2xl font-bold text-pink-600">{data.treatments}</p>
        </div>
        <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
          <p className="text-xs text-gray-600">Pending</p>
          <p className="text-2xl font-bold text-red-600">{data.checkupsPending}</p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderLabel}
            outerRadius={120}
            innerRadius={60}
            fill="#8884d8"
            dataKey="value"
            animationDuration={1000}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            iconType="circle"
            formatter={(value, entry: any) => (
              <span className="text-sm text-gray-700">
                {value} ({entry.payload.value})
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Total Summary */}
      <div className="mt-4 bg-gray-50 p-3 rounded-lg text-center">
        <p className="text-xs text-gray-600">Total Health Activities</p>
        <p className="text-3xl font-bold text-gray-800">{total}</p>
      </div>
    </div>
  );
};

export default HealthRecordsChart;
